<template>
  <div class="test-case-detail">
    <a-page-header
      :title="testCase?.case_name || '测试用例详情'"
      @back="goBack"
    >
      <template #extra>
        <a-space>
          <a-button @click="editCase">编辑</a-button>
          <a-button type="primary" @click="createSnapshot">创建快照</a-button>
          <a-button type="primary" @click="executeTest">立即执行</a-button>
        </a-space>
      </template>
    </a-page-header>

    <a-spin :spinning="loading">
      <a-card style="margin-top: 16px" v-if="testCase">
        <!-- 基本信息 -->
        <a-descriptions title="基本信息" :column="2" bordered>
          <a-descriptions-item label="用例编码">
            <a-tag color="blue">{{ testCase.case_code }}</a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="分类">
            {{ testCase.category || '-' }}
          </a-descriptions-item>
          <a-descriptions-item label="优先级">
            <a-tag :color="getPriorityColor(testCase.priority)">
              {{ getPriorityText(testCase.priority) }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="状态">
            <a-tag :color="testCase.status === 'active' ? 'green' : 'default'">
              {{ testCase.status === 'active' ? '活跃' : '已归档' }}
            </a-tag>
          </a-descriptions-item>
          <a-descriptions-item label="创建时间" :span="2">
            {{ testCase.create_time }}
          </a-descriptions-item>
          <a-descriptions-item label="描述" :span="2">
            {{ testCase.description || '-' }}
          </a-descriptions-item>
          <a-descriptions-item v-if="testCase.case_scope === 'plan'" label="用例范围">
            <a-tag color="purple">整条计划</a-tag>
            <span v-if="testCase.input_data?.steps">({{ testCase.input_data.steps.length }} 步)</span>
          </a-descriptions-item>
          <a-descriptions-item v-if="testCase.case_scope === 'plan' && testCase.source_plan_id" label="来源计划ID">
            {{ testCase.source_plan_id }}
          </a-descriptions-item>
        </a-descriptions>

        <!-- 整条计划：端到端总览 + 步骤列表 -->
        <template v-if="testCase.case_scope === 'plan' && testCase.input_data">
          <a-divider>端到端总览</a-divider>
          <a-descriptions :column="1" bordered size="small">
            <a-descriptions-item label="故障现象">
              {{ testCase.input_data?.plan_input?.fault_phenomenon || testCase.input_data?.fault_phenomenon || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="车型/车辆信息">
              {{ planInputVehicleSummary }}
            </a-descriptions-item>
            <a-descriptions-item label="整体预期输出（最后一步）">
              <a-collapse v-if="lastStepExpectedData" size="small">
                <a-collapse-panel key="1" header="查看最后一步预期 response_data">
                  <pre class="json-display">{{ typeof lastStepExpectedData === 'object' ? JSON.stringify(lastStepExpectedData, null, 2) : lastStepExpectedData }}</pre>
                </a-collapse-panel>
              </a-collapse>
              <span v-else>-</span>
            </a-descriptions-item>
          </a-descriptions>

          <a-divider>步骤列表</a-divider>
          <a-table
            :columns="planStepsColumns"
            :data-source="(testCase.input_data.steps || []).map((s, i) => ({ ...s, _index: i + 1 }))"
            :pagination="false"
            :row-key="planStepRowKey"
            size="small"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'expected_summary'">
                <span v-if="expectedStep(record)">{{ expectedStepSummary(record) }}</span>
                <span v-else>-</span>
              </template>
              <template v-else-if="column.key === 'baseline_snapshot'">
                <template v-if="baselineSnapshotId(record) != null">
                  <a-tag>{{ baselineSnapshotId(record) }}</a-tag>
                  <a-button type="link" size="small" @click="viewSnapshot(baselineSnapshotId(record))">查看</a-button>
                </template>
                <span v-else>-</span>
              </template>
              <template v-else-if="column.key === 'action'">
                <a-button type="link" size="small" @click="openStepDrawer(record)">打开子用例</a-button>
              </template>
            </template>
          </a-table>
          <div v-if="testCase.baseline_step_snapshot_mapping && Object.keys(testCase.baseline_step_snapshot_mapping).length" style="margin-top: 8px;">
            <span class="text-secondary">基线步快照映射：</span>
            <a-tag v-for="(sid, order) in testCase.baseline_step_snapshot_mapping" :key="order">
              步骤{{ order }} → {{ sid }}
            </a-tag>
          </div>
        </template>

        <!-- 统计信息 -->
        <a-divider>执行统计</a-divider>
        <a-row :gutter="16">
          <a-col :span="6">
            <a-statistic title="执行次数" :value="testCase.execution_count || 0" />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="平均分数" 
              :value="testCase.avg_score || 0" 
              :precision="2" 
              suffix="分"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="最高分数" 
              :value="testCase.max_score || 0" 
              :precision="0" 
              suffix="分"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic 
              title="最低分数" 
              :value="testCase.min_score || 0" 
              :precision="0" 
              suffix="分"
            />
          </a-col>
        </a-row>

        <!-- 评分标准（整条计划集合详情不展示，在子用例中查看/编辑） -->
        <template v-if="testCase.case_scope !== 'plan'">
          <a-divider>评分标准</a-divider>
          <a-descriptions :column="1" bordered>
            <a-descriptions-item label="A级标准（优秀）">
              <div class="criteria-text">{{ testCase.pass_criteria?.criteria_a || '-' }}</div>
            </a-descriptions-item>
            <a-descriptions-item label="B级标准（良好）">
              <div class="criteria-text">{{ testCase.pass_criteria?.criteria_b || '-' }}</div>
            </a-descriptions-item>
            <a-descriptions-item label="C级标准（及格）">
              <div class="criteria-text">{{ testCase.pass_criteria?.criteria_c || '-' }}</div>
            </a-descriptions-item>
            <a-descriptions-item label="通过阈值">
              <a-tag color="orange">{{ testCase.pass_criteria?.pass_threshold }}级及以上</a-tag>
            </a-descriptions-item>
          </a-descriptions>
        </template>

        <!-- 配置快照列表 -->
        <a-divider>
          <span>配置快照</span>
          <a-button type="link" size="small" @click="showSnapshotManager">
            管理快照
          </a-button>
        </a-divider>
        
        <a-table
          :columns="snapshotColumns"
          :data-source="snapshots"
          :loading="snapshotsLoading"
          :pagination="false"
          row-key="id"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'snapshot_name'">
              <a-space>
                <span>{{ record.snapshot_name }}</span>
                <a-tag v-if="record.is_baseline" color="gold">基线</a-tag>
              </a-space>
            </template>
            <template v-else-if="column.key === 'action'">
              <a-space>
                <a-button type="link" size="small" @click="viewSnapshot(record.id)">
                  查看
                </a-button>
                <a-button 
                  type="link" 
                  size="small" 
                  @click="executeWithSnapshot(record.id)"
                >
                  执行测试
                </a-button>
              </a-space>
            </template>
          </template>
        </a-table>

        <!-- 执行历史 -->
        <a-divider>
          <span>执行历史</span>
          <a-button type="link" size="small" @click="viewAllResults">
            查看全部
          </a-button>
        </a-divider>

        <a-space style="margin-bottom: 12px" v-if="selectedExecutionKeys.length > 0">
          <a-button 
            type="primary" 
            size="small"
            :disabled="selectedExecutionKeys.length !== 2"
            @click="compareSelectedExecutions"
          >
            对比选中 ({{ selectedExecutionKeys.length }}/2)
          </a-button>
          <a-button size="small" @click="selectedExecutionKeys = []">取消选择</a-button>
        </a-space>

        <a-table
          :columns="executionColumns"
          :data-source="executions"
          :loading="executionsLoading"
          :pagination="{ pageSize: 10 }"
          :row-selection="{ selectedRowKeys: selectedExecutionKeys, onChange: onExecutionSelectChange }"
          row-key="id"
          size="small"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'execution_status'">
              <a-tag :color="getStatusColor(record.execution_status)">
                {{ getStatusText(record.execution_status) }}
              </a-tag>
            </template>
            <template v-else-if="column.key === 'score_level'">
              <a-tag v-if="record.score_level" :color="getScoreLevelColor(record.score_level)">
                {{ record.score_level }}级 ({{ record.score_value }})
              </a-tag>
              <span v-else>-</span>
            </template>
            <template v-else-if="column.key === 'action'">
              <a-button type="link" size="small" @click="viewExecutionDetail(record.id)">
                详情
              </a-button>
            </template>
          </template>
        </a-table>

        <!-- 测试数据（整条计划集合详情不展示，在子用例中查看） -->
        <template v-if="testCase.case_scope !== 'plan'">
          <a-divider>测试数据</a-divider>
          <a-tabs>
            <a-tab-pane key="input" tab="输入数据">
              <a-card size="small">
                <pre class="json-display">{{ JSON.stringify(testCase.input_data, null, 2) }}</pre>
              </a-card>
            </a-tab-pane>
            <a-tab-pane key="expected" tab="预期输出">
              <a-card size="small">
                <pre class="json-display">{{ JSON.stringify(testCase.expected_output, null, 2) }}</pre>
              </a-card>
            </a-tab-pane>
          </a-tabs>
        </template>
      </a-card>
    </a-spin>

    <!-- 快照管理对话框 -->
    <a-modal
      v-model:open="snapshotManagerVisible"
      title="快照管理"
      width="1000px"
      :footer="null"
    >
      <snapshot-manager v-if="testCaseId" :test-case-id="testCaseId" />
    </a-modal>

    <!-- 执行记录对比对话框 -->
    <ExecutionCompareModal
      v-model:open="executionCompareVisible"
      :execution-id1="compareExecutionIds[0]"
      :execution-id2="compareExecutionIds[1]"
    />

    <!-- 快照详情弹窗（与快照管理中查看一致） -->
    <a-modal
      v-model:open="snapshotDetailVisible"
      title="快照详情"
      width="900px"
      :footer="null"
      destroy-on-close
    >
      <a-spin :spinning="snapshotDetailLoading">
        <div v-if="snapshotDetailData" class="snapshot-detail-content">
          <a-descriptions :column="2" bordered>
            <a-descriptions-item label="快照名称" :span="2">
              {{ snapshotDetailData.snapshot_name }}
            </a-descriptions-item>
            <a-descriptions-item label="类型">
              {{ getSnapshotTypeText(snapshotDetailData.snapshot_type) }}
            </a-descriptions-item>
            <a-descriptions-item label="版本">
              {{ snapshotDetailData.snapshot_version || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="Worker">
              {{ snapshotDetailData.worker_name || '-' }}
            </a-descriptions-item>
            <a-descriptions-item label="快照时间">
              {{ snapshotDetailData.snapshot_time }}
            </a-descriptions-item>
          </a-descriptions>

          <a-divider>配置详情</a-divider>
          <a-collapse v-if="snapshotDetailConfig">
            <a-collapse-panel key="model" header="模型配置">
              <pre class="json-display">{{ JSON.stringify(snapshotDetailConfig.model, null, 2) }}</pre>
            </a-collapse-panel>
            <a-collapse-panel key="prompt" header="提示词模板">
              <div>名称: {{ snapshotDetailConfig.prompt_template?.name || '-' }}</div>
              <div>大小: {{ snapshotDetailConfig.prompt_template?.content?.length ?? 0 }} 字符</div>
            </a-collapse-panel>
            <a-collapse-panel key="knowledge" header="知识配置">
              <div>共 {{ snapshotDetailConfig.knowledge?.knowledge_details?.length ?? 0 }} 条知识</div>
              <a-list
                :data-source="snapshotDetailConfig.knowledge?.knowledge_details || []"
                size="small"
              >
                <template #renderItem="{ item }">
                  <a-list-item>{{ item.title }} ({{ item.type_name }})</a-list-item>
                </template>
              </a-list>
            </a-collapse-panel>
            <a-collapse-panel key="output" header="输出参数">
              <pre class="json-display">{{ JSON.stringify(snapshotDetailConfig.output_params, null, 2) }}</pre>
            </a-collapse-panel>
          </a-collapse>
        </div>
      </a-spin>
    </a-modal>

    <!-- 整条计划：单个 Worker 子用例抽屉 -->
    <a-drawer
      v-model:open="stepDrawerVisible"
      :title="currentStepData ? `子用例 - 步骤${currentStepData.step_order} · ${currentStepData.worker_name || '-'}` : '子用例'"
      width="720px"
      :body-style="{ paddingBottom: '80px' }"
      destroy-on-close
      @close="onStepDrawerClose"
    >
      <template v-if="currentStepData">
        <a-descriptions title="基本信息" :column="1" bordered size="small" style="margin-bottom: 16px;">
          <a-descriptions-item label="步骤">{{ currentStepData.step_order }}</a-descriptions-item>
          <a-descriptions-item label="Worker">{{ currentStepData.worker_name || '-' }}</a-descriptions-item>
        </a-descriptions>

        <a-divider>输入数据</a-divider>
        <a-card size="small" style="margin-bottom: 16px;">
          <pre class="json-display">{{ JSON.stringify(currentStepData.stepInput, null, 2) }}</pre>
        </a-card>

        <a-divider>预期输出</a-divider>
        <a-card size="small" style="margin-bottom: 16px;">
          <pre class="json-display">{{ JSON.stringify(currentStepData.stepExpected, null, 2) }}</pre>
        </a-card>

        <a-divider>评分标准（可编辑）</a-divider>
        <a-form layout="vertical" :model="stepCriteriaForm">
          <a-form-item label="A级标准（优秀）">
            <a-textarea v-model:value="stepCriteriaForm.criteria_a" :rows="2" />
          </a-form-item>
          <a-form-item label="B级标准（良好）">
            <a-textarea v-model:value="stepCriteriaForm.criteria_b" :rows="2" />
          </a-form-item>
          <a-form-item label="C级标准（及格）">
            <a-textarea v-model:value="stepCriteriaForm.criteria_c" :rows="2" />
          </a-form-item>
          <a-form-item label="通过阈值">
            <a-select v-model:value="stepCriteriaForm.pass_threshold" style="width: 120px">
              <a-select-option value="A">A</a-select-option>
              <a-select-option value="B">B</a-select-option>
              <a-select-option value="C">C</a-select-option>
            </a-select>
          </a-form-item>
        </a-form>
      </template>
      <template #footer>
        <a-space>
          <a-button @click="stepDrawerVisible = false">关闭</a-button>
          <a-button type="primary" :loading="stepSaveLoading" @click="saveStepCriteria">保存评分标准</a-button>
        </a-space>
      </template>
    </a-drawer>

    <!-- 编辑对话框 -->
    <a-modal
      v-model:open="editDialogVisible"
      title="编辑测试用例"
      width="800px"
      @ok="handleUpdate"
    >
      <a-form :label-col="{ span: 6 }" :wrapper-col="{ span: 18 }">
        <a-form-item label="用例名称" required>
          <a-input v-model:value="editForm.case_name" />
        </a-form-item>
        <a-form-item label="描述">
          <a-textarea v-model:value="editForm.description" :rows="2" />
        </a-form-item>
        <a-form-item label="分类">
          <a-input v-model:value="editForm.category" />
        </a-form-item>
        <a-form-item label="优先级">
          <a-select v-model:value="editForm.priority">
            <a-select-option value="high">高</a-select-option>
            <a-select-option value="medium">中</a-select-option>
            <a-select-option value="low">低</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="状态">
          <a-select v-model:value="editForm.status">
            <a-select-option value="active">活跃</a-select-option>
            <a-select-option value="archived">已归档</a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup>
import { ref, onMounted, reactive, watch, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { getTestCaseDetail, updateTestCase } from '@/api/evalTestCaseV2';
import { getSnapshotsByTestCase, createSnapshot as createSnapshotAPI, getSnapshotDetail } from '@/api/evalSnapshotV2';
import { getExecutionResults } from '@/api/evalExecutionV2';
import SnapshotManager from './components/SnapshotManager.vue';
import ExecutionCompareModal from './components/ExecutionCompareModal.vue';

const route = useRoute();
const router = useRouter();

const testCaseId = ref(null);

// 初始化时获取路由参数
const initTestCaseId = () => {
  const id = route.params.id;
  if (id) {
    testCaseId.value = Number(id);
    console.log('[TestCaseDetail] 测试用例ID:', testCaseId.value);
  } else {
    console.error('[TestCaseDetail] 未获取到测试用例ID');
  }
};
const testCase = ref(null);
const snapshots = ref([]);
const executions = ref([]);
const loading = ref(false);
const snapshotsLoading = ref(false);
const executionsLoading = ref(false);
const snapshotManagerVisible = ref(false);
const editDialogVisible = ref(false);
const executionCompareVisible = ref(false);
const selectedExecutionKeys = ref([]);
const compareExecutionIds = ref([null, null]);

const stepDrawerVisible = ref(false);
const currentStepData = ref(null);
const stepSaveLoading = ref(false);

const snapshotDetailVisible = ref(false);
const snapshotDetailId = ref(null);
const snapshotDetailData = ref(null);
const snapshotDetailLoading = ref(false);
const stepCriteriaForm = reactive({
  criteria_a: '',
  criteria_b: '',
  criteria_c: '',
  pass_threshold: 'B'
});

const editForm = reactive({
  case_name: '',
  description: '',
  category: '',
  priority: 'medium',
  status: 'active'
});

const snapshotColumns = [
  { title: '快照名称', dataIndex: 'snapshot_name', key: 'snapshot_name' },
  { title: '版本', dataIndex: 'snapshot_version', key: 'snapshot_version', width: 100 },
  { title: '快照时间', dataIndex: 'snapshot_time', key: 'snapshot_time', width: 180 },
  { title: '操作', key: 'action', width: 180 }
];

const executionColumns = [
  { title: '执行时间', dataIndex: 'execution_time', key: 'execution_time', width: 180 },
  { title: '快照', dataIndex: 'snapshot_name', key: 'snapshot_name', width: 150 },
  { title: '状态', dataIndex: 'execution_status', key: 'execution_status', width: 100 },
  { title: '评分', dataIndex: 'score_level', key: 'score_level', width: 120 },
  { title: '耗时(ms)', dataIndex: 'execution_duration', key: 'execution_duration', width: 100 },
  { title: '操作', key: 'action', width: 100 }
];

const planStepsColumns = [
  { title: '步骤', dataIndex: 'step_order', key: 'step_order', width: 80 },
  { title: 'Worker', dataIndex: 'worker_name', key: 'worker_name', width: 180 },
  { title: '预期摘要', key: 'expected_summary', width: 200, ellipsis: true },
  { title: '基线快照ID', key: 'baseline_snapshot', width: 120 },
  { title: '操作', key: 'action', width: 120 }
];

function expectedStep(record) {
  const steps = testCase.value?.expected_output?.steps;
  if (!Array.isArray(steps)) return null;
  return steps.find(s => Number(s.step_order) === Number(record.step_order));
}

function expectedStepSummary(record) {
  const step = expectedStep(record);
  if (!step || !step.response_data) return '-';
  const s = typeof step.response_data === 'string' ? step.response_data : JSON.stringify(step.response_data);
  return s.length > 80 ? s.slice(0, 80) + '...' : s;
}

function baselineSnapshotId(record) {
  const mapping = testCase.value?.baseline_step_snapshot_mapping;
  if (!mapping || typeof mapping !== 'object') return null;
  return mapping[String(record.step_order)] ?? mapping[String(record._index)] ?? null;
}

function planStepRowKey(record) {
  return `${record.step_order}-${record.worker_id || record._index}`;
}

// 整条计划总览：车型/车辆信息摘要（来自 plan_input）
const planInputVehicleSummary = computed(() => {
  const planInput = testCase.value?.input_data?.plan_input;
  if (!planInput || typeof planInput !== 'object') return '-';
  const parts = [];
  if (planInput.vehicle_type_name || planInput.vehicle_model_name) {
    parts.push([planInput.vehicle_type_name, planInput.vehicle_model_name].filter(Boolean).join(' '));
  }
  if (planInput.engine_tag_value) parts.push(`发动机: ${planInput.engine_tag_value}`);
  if (planInput.control_unit_model || planInput.control_unit_type) {
    parts.push([planInput.control_unit_type, planInput.control_unit_model].filter(Boolean).join(' '));
  }
  return parts.length ? parts.join('；') : '-';
});

// 整条计划总览：最后一步的预期 response_data
const lastStepExpectedData = computed(() => {
  const steps = testCase.value?.expected_output?.steps;
  if (!Array.isArray(steps) || steps.length === 0) return null;
  const last = steps[steps.length - 1];
  return last?.response_data ?? null;
});

function openStepDrawer(record) {
  const steps = testCase.value?.input_data?.steps;
  const expectedSteps = testCase.value?.expected_output?.steps;
  if (!Array.isArray(steps) || steps.length === 0) return;
  const stepIndex = record._index != null ? record._index - 1 : steps.findIndex(s => Number(s.step_order) === Number(record.step_order));
  if (stepIndex < 0) return;
  const stepInput = steps[stepIndex] || {};
  const stepExpected = Array.isArray(expectedSteps) ? expectedSteps[stepIndex] : null;
  const passCriteria = stepInput.pass_criteria || testCase.value.pass_criteria || {};
  currentStepData.value = {
    stepIndex,
    step_order: record.step_order,
    worker_name: record.worker_name,
    stepInput,
    stepExpected
  };
  stepCriteriaForm.criteria_a = passCriteria.criteria_a ?? '';
  stepCriteriaForm.criteria_b = passCriteria.criteria_b ?? '';
  stepCriteriaForm.criteria_c = passCriteria.criteria_c ?? '';
  stepCriteriaForm.pass_threshold = passCriteria.pass_threshold ?? 'B';
  stepDrawerVisible.value = true;
}

function onStepDrawerClose() {
  currentStepData.value = null;
}

async function saveStepCriteria() {
  if (!currentStepData.value || !testCase.value?.input_data?.steps) return;
  const { stepIndex } = currentStepData.value;
  const steps = [...testCase.value.input_data.steps];
  if (!steps[stepIndex]) return;
  steps[stepIndex] = {
    ...steps[stepIndex],
    pass_criteria: {
      criteria_a: stepCriteriaForm.criteria_a,
      criteria_b: stepCriteriaForm.criteria_b,
      criteria_c: stepCriteriaForm.criteria_c,
      pass_threshold: stepCriteriaForm.pass_threshold,
      auto_judge: steps[stepIndex].pass_criteria?.auto_judge ?? true
    }
  };
  stepSaveLoading.value = true;
  try {
    await updateTestCase(testCaseId.value, {
      input_data: { ...testCase.value.input_data, steps }
    });
    message.success('评分标准已保存');
    testCase.value.input_data = { ...testCase.value.input_data, steps };
    stepDrawerVisible.value = false;
  } catch (e) {
    message.error('保存失败：' + (e.message || e));
  } finally {
    stepSaveLoading.value = false;
  }
}

const fetchTestCase = async () => {
  loading.value = true;
  try {
    const res = await getTestCaseDetail(testCaseId.value);
    if (res.code === 200) {
      testCase.value = res.data;
    } else {
      message.error(res.message || '获取测试用例失败');
    }
  } catch (error) {
    message.error('获取测试用例失败：' + error.message);
  } finally {
    loading.value = false;
  }
};

const fetchSnapshots = async () => {
  snapshotsLoading.value = true;
  try {
    const res = await getSnapshotsByTestCase(testCaseId.value);
    if (res.code === 200) {
      snapshots.value = (res.data || []).slice(0, 5); // 只显示最近5个
    }
  } catch (error) {
    console.error('获取快照失败:', error);
  } finally {
    snapshotsLoading.value = false;
  }
};

const fetchExecutions = async () => {
  executionsLoading.value = true;
  try {
    const res = await getExecutionResults({
      test_case_id: testCaseId.value,
      pageSize: 10
    });
    if (res.code === 200) {
      executions.value = res.data?.list || [];
    }
  } catch (error) {
    console.error('获取执行历史失败:', error);
  } finally {
    executionsLoading.value = false;
  }
};

const goBack = () => {
  router.back();
};

const editCase = () => {
  Object.assign(editForm, {
    case_name: testCase.value.case_name,
    description: testCase.value.description,
    category: testCase.value.category,
    priority: testCase.value.priority,
    status: testCase.value.status
  });
  editDialogVisible.value = true;
};

const handleUpdate = async () => {
  try {
    await updateTestCase(testCaseId.value, editForm);
    message.success('更新成功');
    editDialogVisible.value = false;
    fetchTestCase();
  } catch (error) {
    message.error('更新失败：' + error.message);
  }
};

const createSnapshot = async () => {
  try {
    message.loading('正在创建快照...', 0);
    const res = await createSnapshotAPI({
      test_case_id: testCaseId.value,
      snapshot_name: `快照_${new Date().toLocaleString()}`,
      snapshot_type: 'custom'
    });
    message.destroy();
    if (res.code === 200 && res.data?.created_count != null) {
      message.success(res.message || `已为整条计划创建 ${res.data.created_count} 个步快照`);
      if (testCase.value) testCase.value.baseline_step_snapshot_mapping = res.data.baseline_step_snapshot_mapping;
    } else {
      message.success(res.message || '快照创建成功');
    }
    fetchSnapshots();
    if (testCaseId.value) fetchTestCase();
  } catch (error) {
    message.destroy();
    message.error('创建快照失败：' + error.message);
  }
};

const executeTest = () => {
  message.info('请通过测试计划执行测试');
  router.push('/eval-v2/test-plans');
};

const executeWithSnapshot = (snapshotId) => {
  message.info(`将使用快照${snapshotId}执行测试，请先创建测试计划`);
  router.push('/eval-v2/test-plans');
};

const showSnapshotManager = () => {
  snapshotManagerVisible.value = true;
};

const viewSnapshot = (id) => {
  snapshotDetailId.value = id;
  snapshotDetailVisible.value = true;
};

async function fetchSnapshotDetail() {
  if (!snapshotDetailId.value) return;
  snapshotDetailLoading.value = true;
  try {
    const res = await getSnapshotDetail(snapshotDetailId.value);
    if (res.code === 200) {
      snapshotDetailData.value = res.data;
    } else {
      message.error(res.message || '获取快照详情失败');
    }
  } catch (e) {
    message.error('获取快照详情失败：' + (e.message || e));
  } finally {
    snapshotDetailLoading.value = false;
  }
}

watch(snapshotDetailVisible, (visible) => {
  if (visible && snapshotDetailId.value) {
    fetchSnapshotDetail();
  } else if (!visible) {
    snapshotDetailData.value = null;
    snapshotDetailId.value = null;
  }
});

const snapshotDetailConfig = computed(() => {
  const d = snapshotDetailData.value?.config_data;
  if (d == null) return null;
  if (typeof d === 'object') return d;
  try {
    return typeof d === 'string' ? JSON.parse(d) : null;
  } catch {
    return null;
  }
});

function getSnapshotTypeText(type) {
  const texts = { baseline: '基线', latest: '最新', custom: '自定义' };
  return texts[type] || type || '-';
}

const viewAllResults = () => {
  router.push(`/eval-v2/results?test_case_id=${testCaseId.value}`);
};

const viewExecutionDetail = (id) => {
  router.push(`/eval-v2/execution/${id}`);
};

const onExecutionSelectChange = (keys) => {
  if (keys.length > 2) {
    message.warning('最多选择2条记录进行对比');
    return;
  }
  selectedExecutionKeys.value = keys;
};

const compareSelectedExecutions = () => {
  if (selectedExecutionKeys.value.length !== 2) {
    message.warning('请选择2条记录进行对比');
    return;
  }
  compareExecutionIds.value = [...selectedExecutionKeys.value];
  executionCompareVisible.value = true;
};

const getPriorityColor = (priority) => {
  const colors = { high: 'red', medium: 'orange', low: 'green' };
  return colors[priority] || 'default';
};

const getPriorityText = (priority) => {
  const texts = { high: '高', medium: '中', low: '低' };
  return texts[priority] || priority;
};

const getStatusColor = (status) => {
  const colors = {
    success: 'success',
    failed: 'error',
    timeout: 'warning'
  };
  return colors[status] || 'default';
};

const getStatusText = (status) => {
  const texts = {
    success: '成功',
    failed: '失败',
    timeout: '超时'
  };
  return texts[status] || status;
};

const getScoreLevelColor = (level) => {
  const colors = { A: 'success', B: 'processing', C: 'warning' };
  return colors[level] || 'default';
};

onMounted(() => {
  initTestCaseId();
  if (testCaseId.value) {
    fetchTestCase();
    fetchSnapshots();
    fetchExecutions();
  }
});
</script>

<style scoped>
.test-case-detail {
  padding: 24px;
}

.criteria-text {
  white-space: pre-wrap;
  line-height: 1.6;
}

.json-display {
  background: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  max-height: 400px;
  overflow: auto;
  font-size: 12px;
}
</style>

